import { useCallback, useEffect, useState } from "react";
import Page from "lib/components/layout/Page/Page";
import { Box, Typography, Button } from "@mui/material";
import { sxStyles } from "./Dashboard.styles";
import PaymentFooterLinks from "../common/PaymentFooterLinks/PaymentFooterLinks";
import ProfileHeader from "../common/ProfileHeader/ProfileHeader";
import IMAGES from "lib/assets/images";
import { useNavigate } from "react-router";
import { PATHS } from "lib/routing";
import ErrorDisplay from "lib/components/error/ErrorDisplay/ErrorDisplay";
import { logEventToGoogleAnalytics } from "lib/util/GoogleAnalyticsUtil/logEvent";
import { EVENTS } from "lib/util/GoogleAnalyticsUtil/events";
import PullToRefreshWrapper from "lib/components/pullToRefreshWrapper/PullToRefreshWrapper";
import AnnouncementsPanelSkeleton from "lib/components/notice/AnnouncementPanel/AnnouncementPanelSkeleton";
import BillCardSkeleton from "./BillCard/BillCardSkeleton/BillCardSkeleton";
import SharpNoticePanel from "lib/components/notice/RoundedNoticePanel/SharpNoticePanel";
import toTitleCase from "lib/util/StringUtil/toTitleCase/toTitleCase";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import BillCard from "./BillCard/BillCard";

import {
  setSelectedAddedBillsIndex,
  setSelectedOutstandingBillsIndex,
  setBillsToPay,
  setExpandedOutstandingBillsListFlag,
  setExpandedPaidBillsListFlag,
  setPaidBills,
  setShouldUpdate,
  setTransactionHistory,
} from "ui/payment/ducks";
import { useGetMessageAction } from "lib/routing/messageChannel/hooks/useGetMessageAction";
import { selectPayments } from "ui/payment/ducks/selectors";
import { PAYMENT_BILL_DASHBOARD_TEXT_KEY } from "api/shared/GetDocumentByType/getDocumentByType.mapper";
import {
  fetchDocumentForAllTypes,
  fetchOutstandingBills,
  fetchSystemSettingsPaymentLandingMessage,
} from "ui/payment/ducks/thunks";
import ConfirmationModal from "lib/components/modals/ConfirmationModal/ConfirmationModal";
import MapRawStringToReactElement from "lib/util/ReactComponentUtil/mapRawStringToReactElement/MapRawStringtoReactElement";
import { enqueueMessage } from "lib/redux/navigation/navigationSlice";
import { MessageActions } from "lib/routing/messageChannel/messageActions";
import { MobileStorageKeys } from "lib/routing/messageChannel/messageActions.types";

const DEFAULT_NUMBER_OF_REPORT_SUMMARIES_TO_SHOW = 2;
const OUTSTANDING_BILL_TITLE = "Latest Outstanding Bills";
const OTHER_ADDED_BILL_TITLE = "Added Bills";

const Dashboard = () => {
  const navigate = useNavigate();
  const classes = sxStyles();
  const dispatch = useAppDispatch();

  // Redux States
  const {
    shouldUpdate: shouldUpdateOutstandingBills,
    isLoading: isLoadingOutstandingBills,
    hasErrored: hasErroredOutstandingBills,
    errorMessage: errorMessageOutstandingBills,
    allOutstandingBills,
    expandedOutstandingBillsListFlag,
  } = useAppSelector(selectPayments).outstandingBills;

  const otherAddedBillList =
    useAppSelector(selectPayments).addedBills.allAddedBills;
  const paymentLandingMessage =
    useAppSelector(selectPayments).systemSettings.value.paymentLandingMessage;
  const isLoadingDisclaimer =
    useAppSelector(selectPayments).documentByType.isLoading;
  const disclaimer =
    useAppSelector(selectPayments).documentByType.documentByTypeMap[
      PAYMENT_BILL_DASHBOARD_TEXT_KEY
    ];

  const numberToShow = expandedOutstandingBillsListFlag
    ? allOutstandingBills.length
    : DEFAULT_NUMBER_OF_REPORT_SUMMARIES_TO_SHOW;

  // Local States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const { messageActionObj, consumeMessageActionObj } = useGetMessageAction();

  useEffect(() => {
    if (!paymentLandingMessage) {
      dispatch(fetchSystemSettingsPaymentLandingMessage());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (paymentLandingMessage?.IsActive && paymentLandingMessage?.Message) {
      dispatch(
        enqueueMessage(
          MessageActions.mobileStoreGet({
            key: "isDoNotShowAgain",
          }),
        ),
      );
    }
  }, [
    dispatch,
    paymentLandingMessage?.IsActive,
    paymentLandingMessage?.Message,
  ]);

  useEffect(() => {
    if (messageActionObj?.action === "mobileStoreGetResult") {
      const mobileValue = messageActionObj.params
        ?.value as MobileStorageKeys["isDoNotShowAgain"];
      const isDoNotShowAgain = mobileValue === "true";
      const willShowModal = !isDoNotShowAgain;
      setIsModalOpen(willShowModal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageActionObj?.action]);

  const refreshData = useCallback(() => {
    // clearing existing data when back from transaction history page
    dispatch(setTransactionHistory([]));
    // clearing existing data when back from paid bills page
    dispatch(setPaidBills([]));
    // clearing amount to pay when back from details page
    dispatch(setBillsToPay([]));
    // changing paid bills flag as false when back from paid bills page
    setExpandedPaidBillsListFlag(false);
    logEventToGoogleAnalytics(EVENTS.VIEW_PAYMENT_LANDING);
    // prevent reloading when back from bill details page
    if (shouldUpdateOutstandingBills) {
      dispatch(fetchDocumentForAllTypes());
      dispatch(fetchOutstandingBills());
    }
  }, [shouldUpdateOutstandingBills, dispatch]);

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOutstandingBills]);

  // Mobile will send a message to force refresh when user switches modules from bottom tab
  useEffect(() => {
    if (messageActionObj?.action === "refreshLanding") {
      window.location.reload();
      consumeMessageActionObj();
    }
  }, [consumeMessageActionObj, messageActionObj?.action, refreshData]);

  return (
    <Page>
      <PullToRefreshWrapper
        refreshData={() => {
          dispatch(setShouldUpdate(true));
          refreshData();
        }}
      >
        <ProfileHeader title="'s Bills" />

        {isLoadingDisclaimer ? (
          <AnnouncementsPanelSkeleton />
        ) : disclaimer ? (
          <Box m={2}>
            <SharpNoticePanel showRaw={false} children={disclaimer} />
          </Box>
        ) : null}

        {isLoadingOutstandingBills ? (
          <BillCardSkeleton />
        ) : hasErroredOutstandingBills ? (
          <ErrorDisplay
            errorMessage={errorMessageOutstandingBills}
            onTryAgain={() => {
              dispatch(fetchOutstandingBills());
            }}
          />
        ) : (
          <>
            <Box m={2}>
              {allOutstandingBills && allOutstandingBills.length > 0 ? (
                <>
                  <Typography sx={classes.title}>
                    {toTitleCase(OUTSTANDING_BILL_TITLE)}
                  </Typography>

                  {allOutstandingBills
                    .slice(0, numberToShow)
                    .map((bill, index) => {
                      return (
                        <BillCard
                          key={bill.InvoiceNumber}
                          addedBill={null}
                          outstandingBill={bill}
                          onClickBill={function (
                            selectedBillReferenceNumber: string | null,
                          ): void {
                            dispatch(setShouldUpdate(false));

                            dispatch(
                              setSelectedOutstandingBillsIndex(
                                selectedBillReferenceNumber,
                              ),
                            );
                          }}
                        />
                      );
                    })}
                </>
              ) : (
                allOutstandingBills.length === 0 &&
                otherAddedBillList.length === 0 && (
                  <Box align-items="center" justifyContent="center" mt={0}>
                    <Box sx={classes.noRecordBox}>
                      <Box sx={classes.noRecordImage}>
                        <img
                          src={IMAGES.payment.NoOutstandingBills}
                          alt="No Outstanding Bills"
                        />
                      </Box>
                      <Box sx={classes.noRecordTitle}>
                        You have no outstanding bills.
                      </Box>
                    </Box>
                  </Box>
                )
              )}

              {!expandedOutstandingBillsListFlag &&
                allOutstandingBills.length >
                  DEFAULT_NUMBER_OF_REPORT_SUMMARIES_TO_SHOW && (
                  <Box sx={classes.viewPastRequestTextBox}>
                    <Box
                      sx={classes.viewAllOutStandingContainer}
                      onClick={() => {
                        dispatch(setExpandedOutstandingBillsListFlag(true));
                      }}
                    >
                      <Typography sx={classes.viewOutStandingText}>
                        View all outstanding bills
                      </Typography>

                      <Box
                        sx={classes.arrowRightIcon}
                        src={IMAGES.general.ArrowRightIcon}
                        alt="View all outstanding bills"
                        component="img"
                      />
                    </Box>
                  </Box>
                )}
            </Box>

            {otherAddedBillList && otherAddedBillList.length > 0 ? (
              <Box m={2}>
                <Typography sx={classes.title}>
                  {OTHER_ADDED_BILL_TITLE}
                </Typography>

                {otherAddedBillList.map((bill, index) => {
                  return (
                    <BillCard
                      key={bill.InvoiceNumber}
                      addedBill={bill}
                      outstandingBill={null}
                      onClickBill={function (
                        selectedBillReferenceNumber: string | null,
                      ): void {
                        dispatch(setShouldUpdate(false));
                        dispatch(
                          setSelectedAddedBillsIndex(bill.InvoiceNumber),
                        );
                      }}
                    />
                  );
                })}
              </Box>
            ) : null}
            {(allOutstandingBills.length !== 0 ||
              otherAddedBillList.length !== 0) && (
              <Box sx={classes.cardButtons}>
                <Button
                  variant="contained"
                  sx={classes.fullButton}
                  onClick={() => {
                    logEventToGoogleAnalytics(
                      EVENTS.SELECT_MAKE_PAYMENT_LANDING,
                    );
                    navigate(PATHS.PAYMENT_BILL_SUMMARY.path);
                  }}
                  title="button"
                >
                  Pay all bills
                </Button>
              </Box>
            )}
          </>
        )}

        <PaymentFooterLinks />
      </PullToRefreshWrapper>
      {!!paymentLandingMessage?.Message && !!paymentLandingMessage?.Title && (
        <ConfirmationModal
          title={paymentLandingMessage?.Title}
          body={MapRawStringToReactElement(paymentLandingMessage?.Message)}
          open={isModalOpen}
          nextButtonText={"OK"}
          onClickNext={() => {
            dispatch(
              enqueueMessage(
                MessageActions.mobileStoreSet({
                  key: "isDoNotShowAgain",
                  value: isChecked ? "true" : "false",
                }),
              ),
            );

            setIsModalOpen(false);
          }}
          hideCancelButton
          checkBoxText="Do not show again"
          onCheck={() => setIsChecked(!isChecked)}
          isChecked={isChecked}
        />
      )}
    </Page>
  );
};

export default Dashboard;
